// Case Documents Page
// BP-10-04: Attorney Frontend

import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, Share2 } from 'lucide-react';
import { useCase, useSharedDocuments, useDownloadDocument } from '@/features/attorney/hooks/useAttorney';
import { SharedDocumentsList, CaseStatusBadge } from '../components';
import type { CaseStatus } from '@/features/attorney/types/attorney';

export function CaseDocumentsPage() {
  const { caseId } = useParams<{ caseId: string }>();
  
  const { data: caseData } = useCase(caseId!);
  const { data: documents, isLoading } = useSharedDocuments(caseId!);
  const downloadMutation = useDownloadDocument();

  const handleDownload = async (documentId: string) => {
    const url = await downloadMutation.mutateAsync(documentId);
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link
            to={`/attorney/cases/${caseId}`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-semibold text-navy">Shared Documents</h1>
              {caseData && (
                <CaseStatusBadge status={caseData.status as CaseStatus} />
              )}
            </div>
            <p className="text-gray-600">
              {caseData?.family?.name || 'Case'} •{' '}
              Documents shared by the family
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
            <Download className="w-4 h-4" />
            Export All
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Share2 className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-800">
              Family Shared Documents
            </h4>
            <p className="text-sm text-blue-700 mt-1">
              These documents have been shared by the family through their portal.
              Medical records, IEPs, and other relevant documents appear here when
              the family grants access.
            </p>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <SharedDocumentsList
          documents={documents || []}
          onDownload={handleDownload}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

export default CaseDocumentsPage;


